import React from "react";

import type { LayoutSyncEvent, LayoutSyncEventType, LayoutSyncNotice } from "./types";

export const LAYOUT_SYNC_CHANNEL_NAME = "seat-studio-sync";

const REMOTE_DIRTY_MESSAGE = "다른 탭에서 이 좌석도를 수정 중입니다.";
const REMOTE_SAVED_MESSAGE = "다른 탭에서 최신본을 저장/발행했습니다. 현재 탭은 자동 갱신되지 않습니다.";

const createTabId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `tab-${Math.random().toString(36).slice(2, 10)}`;
};

type UseLayoutSyncNoticeParams = {
  reservationPackageId: number | null;
  layoutId: number | null;
  isDirty: boolean;
  onReloadLatest: () => void | Promise<void>;
};

export const useLayoutSyncNotice = ({
  reservationPackageId,
  layoutId,
  isDirty,
  onReloadLatest,
}: UseLayoutSyncNoticeParams) => {
  const channelRef = React.useRef<BroadcastChannel | null>(null);
  const tabIdRef = React.useRef(createTabId());
  const [notice, setNotice] = React.useState<LayoutSyncNotice | null>(null);

  const dismissNotice = React.useCallback(() => {
    setNotice(null);
  }, []);

  React.useEffect(() => {
    if (typeof BroadcastChannel === "undefined") {
      channelRef.current = null;
      return undefined;
    }

    const channel = new BroadcastChannel(LAYOUT_SYNC_CHANNEL_NAME);
    channelRef.current = channel;

    channel.onmessage = (event: MessageEvent<LayoutSyncEvent>) => {
      const payload = event.data;
      if (!payload) {
        return;
      }
      if (payload.senderTabId === tabIdRef.current) {
        return;
      }
      if (!reservationPackageId || payload.reservationPackageId !== reservationPackageId) {
        return;
      }
      if (layoutId && payload.layoutId && payload.layoutId !== layoutId) {
        return;
      }

      if (payload.eventType === "draft-dirty") {
        setNotice({
          tone: "warning",
          message: REMOTE_DIRTY_MESSAGE,
          actionLabel: null,
        });
        return;
      }

      setNotice({
        tone: "info",
        message: REMOTE_SAVED_MESSAGE,
        actionLabel: "최신본 다시 불러오기",
        onAction: async () => {
          await onReloadLatest();
          setNotice(null);
        },
      });
    };

    return () => {
      channel.close();
      if (channelRef.current === channel) {
        channelRef.current = null;
      }
    };
  }, [layoutId, onReloadLatest, reservationPackageId]);

  React.useEffect(() => {
    setNotice(null);
  }, [reservationPackageId]);

  const broadcast = React.useCallback(
    ({ eventType }: { eventType: LayoutSyncEventType }) => {
      if (!channelRef.current || !reservationPackageId) {
        return;
      }

      const payload: LayoutSyncEvent = {
        reservationPackageId,
        layoutId,
        eventType,
        updatedAt: new Date().toISOString(),
        senderTabId: tabIdRef.current,
      };
      channelRef.current.postMessage(payload);
    },
    [layoutId, reservationPackageId],
  );

  return {
    notice,
    dismissNotice,
    broadcast,
    isDirty,
  };
};

export type { LayoutSyncEvent, LayoutSyncEventType, LayoutSyncNotice } from "./types";
